import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ShieldCheck, ArrowLeft, ExternalLink } from 'lucide-react';

const LegalPageLayout = ({ title, subtitle, lastUpdated, children, sections = [] }) => {
  const navigate = useNavigate();
  const appName = process.env.REACT_APP_APP_NAME || 'CRM Automax';
  const [activeSection, setActiveSection] = useState('');
  const contentRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: '-20% 0px -75% 0px' }
    );
    document.querySelectorAll('[data-section]').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const legalLinks = [
    { label: 'Privacy Policy', path: '/privacy-policy' },
    { label: 'Terms & Conditions', path: '/terms' },
    { label: 'Payment Policy', path: '/payment-policy' },
    { label: 'Cookie Policy', path: '/cookie-policy' },
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: '#ffffff',
      fontFamily: "'Inter', -apple-system, sans-serif",
      color: '#1e293b',
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
        @keyframes fadeUp { from{opacity:0;transform:translateY(15px);}to{opacity:1;transform:translateY(0);} }
        .legal-section { scroll-margin-top: 100px; padding-bottom: 40px; }
        .legal-section h2 { font-size:24px; font-weight:800; color:#1e293b; margin-bottom:20px; padding-bottom:12px; border-bottom:1px solid #f1f5f9; }
        .legal-section h3 { font-size:17px; font-weight:700; color:#3b82f6; margin:24px 0 12px; }
        .legal-section p { font-size:15px; color:#475569; line-height:1.75; margin-bottom:16px; }
        .legal-section ul, .legal-section ol { padding-left:22px; margin-bottom:16px; }
        .legal-section li { font-size:15px; color:#475569; line-height:1.75; margin-bottom:8px; }
        .legal-section a { color:#3b82f6; text-decoration:none; font-weight: 500; }
        .legal-section a:hover { text-decoration:underline; }
        .legal-highlight { background:#eff6ff; border:1px solid #dbeafe; border-radius:12px; padding:20px; margin:24px 0; }
        .legal-highlight p { margin-bottom:0; color:#1d4ed8 !important; font-weight: 500; }
        .legal-table { width:100%; border-collapse:collapse; margin:24px 0; border: 1px solid #f1f5f9; }
        .legal-table th { background:#f8fafc; color:#475569; font-size:12px; font-weight:700; text-transform:uppercase; letter-spacing:0.05em; padding:12px 16px; text-align:left; border-bottom: 1px solid #f1f5f9; }
        .legal-table td { padding:12px 16px; font-size:14px; color:#64748b; border-bottom:1px solid #f1f5f9; }
        .legal-table tr:hover td { background:#f8fafc; }
        .toc-item { display:block; padding:8px 12px; border-radius:8px; font-size:14px; color:#64748b; text-decoration:none; cursor:pointer; transition:all 0.2s ease; border:none; background:none; width:100%; text-align:left; margin-bottom: 2px; }
        .toc-item:hover { color:#1e293b; background:#f1f5f9; }
        .toc-item.active { color:#3b82f6; background:#eff6ff; font-weight:600; }
        .back-btn:hover { background:#f1f5f9 !important; border-color: #cbd5e1 !important; color: #1e293b !important; }
      `}</style>

      {/* ── Top Navigation ── */}
      <nav style={{
        position: 'sticky', top: 0, zIndex: 100,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 40px', height: '64px',
        background: '#ffffff',
        borderBottom: '1px solid #f1f5f9',
        boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ width: '34px', height: '34px', borderRadius: '10px', background: 'linear-gradient(135deg, #3b82f6, #2563eb)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ShieldCheck size={18} color="white" />
          </div>
          <span style={{ fontWeight: '800', fontSize: '17px', color: '#1e293b' }}>{appName}</span>
        </div>

        <button
          className="back-btn"
          onClick={() => navigate(-1)}
          style={{
            display: 'flex', alignItems: 'center', gap: '7px',
            background: '#ffffff', border: '1px solid #e2e8f0',
            color: '#64748b', padding: '8px 18px', borderRadius: '10px',
            fontSize: '13px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s ease'
          }}
        >
          <ArrowLeft size={15} /> Back
        </button>
      </nav>

      <div className="legal-layout-container" style={{ display: 'flex', maxWidth: '1180px', margin: '0 auto', padding: '0 24px' }}>

        {/* ── Sidebar TOC ── */}
        {sections.length > 0 && (
          <aside className="legal-sidebar" style={{
            width: '260px', flexShrink: 0, position: 'sticky',
            top: '84px', alignSelf: 'flex-start',
            padding: '40px 20px 40px 0', display: 'block',
          }}>
            <div style={{ fontSize: '12px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '16px', paddingLeft: '12px' }}>
              Table of Contents
            </div>
            {sections.map((s) => (
              <button
                key={s.id}
                className={`toc-item ${activeSection === s.id ? 'active' : ''}`}
                onClick={() => scrollToSection(s.id)}
              >
                {s.label}
              </button>
            ))}
          </aside>
        )}

        {/* ── Main Content ── */}
        <main
          ref={contentRef}
          className="legal-main-content"
          style={{ flex: 1, padding: '56px 0 80px 48px', animation: 'fadeUp 0.6s ease' }}
        >
          {/* Header */}
          <div style={{ marginBottom: '56px' }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              background: '#eff6ff', border: '1px solid #dbeafe',
              padding: '6px 16px', borderRadius: '100px', marginBottom: '24px',
              fontSize: '13px', fontWeight: '600', color: '#2563eb',
            }}>
              <ShieldCheck size={14} /> Official Policy
            </div>
            <h1 style={{ fontSize: '42px', fontWeight: '800', color: '#1e293b', lineHeight: 1.1, marginBottom: '16px' }}>
              {title}
            </h1>
            {subtitle && (
              <p style={{ fontSize: '18px', color: '#64748b', marginBottom: '20px', lineHeight: 1.5 }}>{subtitle}</p>
            )}
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              background: '#f8fafc', border: '1px solid #f1f5f9',
              padding: '8px 16px', borderRadius: '10px', fontSize: '13px', color: '#64748b',
            }}>
              Revision Date: <strong style={{ color: '#475569' }}>{lastUpdated}</strong>
            </div>
          </div>

          {/* Content */}
          <div className="legal-content">
            {children}
          </div>

          {/* ── Footer cross-links ── */}
          <div style={{
            marginTop: '80px', paddingTop: '40px',
            borderTop: '1px solid #f1f5f9',
          }}>
            <div style={{ fontSize: '14px', color: '#94a3b8', marginBottom: '20px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Explore other policies
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
              {legalLinks.map((lnk) => (
                <Link
                  key={lnk.path}
                  to={lnk.path}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    padding: '10px 20px', borderRadius: '12px',
                    background: '#ffffff', border: '1px solid #e2e8f0',
                    fontSize: '14px', color: '#475569', textDecoration: 'none', transition: 'all 0.2s ease',
                    fontWeight: '500',
                  }}
                >
                  {lnk.label} <ExternalLink size={13} />
                </Link>
              ))}
            </div>
          </div>

          {/* Contact strip */}
          <div style={{
            marginTop: '40px', padding: '24px 32px',
            background: '#f8fafc', border: '1px solid #f1f5f9',
            borderRadius: '16px',
          }}>
            <p style={{ fontSize: '14px', color: '#64748b', margin: 0, lineHeight: 1.6 }}>
              Have questions or concerns about these terms? Our compliance team is here to help. Reach out at{' '}
              <a href="mailto:info@westernflex.com" style={{ color: '#3b82f6', fontWeight: '600' }}>info@westernflex.com</a>
            </p>
          </div>
        </main>
      </div>
    </div>
  );
};

export default LegalPageLayout;
